// IPZoneContext — shared state for the Wansight-style sidebar browser
// (IP Addresses tree + IP Groups list) and the /zones page.
//
// Both the sidebar and the config page read/write the "active zone" via
// this context so selection persists across navigations. Nodes of the
// active zone are fetched once and cached here.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, type Zone, type ZoneNode } from "../api";

interface IPZoneCtx {
  zones:        Zone[];
  zonesLoading: boolean;

  activeZoneID: number | null;
  setActiveZoneID: (id: number | null) => void;

  activeNodes:  ZoneNode[];
  nodesLoading: boolean;

  /** Force re-fetch of zones + active nodes. Call after create/update/delete. */
  refresh: () => void;

  /** Force re-fetch of just the active zone's nodes. */
  refreshNodes: () => void;
}

const IPZoneContext = createContext<IPZoneCtx | null>(null);

const LS_KEY = "sflow.activeZoneID";

export function IPZoneProvider({ children }: { children: ReactNode }) {
  const [zones, setZones] = useState<Zone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(false);

  const [activeZoneID, setActiveZoneIDState] = useState<number | null>(() => {
    const raw = localStorage.getItem(LS_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  });

  const [activeNodes, setActiveNodes] = useState<ZoneNode[]>([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const nodesReqToken = useRef(0);

  const setActiveZoneID = useCallback((id: number | null) => {
    setActiveZoneIDState(id);
    if (id == null) localStorage.removeItem(LS_KEY);
    else localStorage.setItem(LS_KEY, String(id));
  }, []);

  const loadZones = useCallback(async () => {
    setZonesLoading(true);
    try {
      const zs = await api.zones();
      setZones(zs);
      // If saved active zone no longer exists, pick the first one.
      setActiveZoneIDState((prev) => {
        if (prev != null && zs.some((z) => z.id === prev)) return prev;
        const fallback = zs.length > 0 ? zs[0].id : null;
        if (fallback != null) localStorage.setItem(LS_KEY, String(fallback));
        else localStorage.removeItem(LS_KEY);
        return fallback;
      });
    } catch (e) {
      console.error("IPZoneContext: loadZones", e);
    } finally {
      setZonesLoading(false);
    }
  }, []);

  const loadNodes = useCallback(async (zoneID: number | null) => {
    if (zoneID == null) {
      setActiveNodes([]);
      return;
    }
    const myToken = ++nodesReqToken.current;
    setNodesLoading(true);
    try {
      const ns = await api.zoneNodes(zoneID);
      if (myToken !== nodesReqToken.current) return;
      setActiveNodes(ns);
    } catch (e) {
      if (myToken !== nodesReqToken.current) return;
      console.error("IPZoneContext: loadNodes", e);
      setActiveNodes([]);
    } finally {
      if (myToken === nodesReqToken.current) setNodesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  useEffect(() => {
    loadNodes(activeZoneID);
  }, [activeZoneID, loadNodes]);

  const refresh = useCallback(() => {
    loadZones();
    loadNodes(activeZoneID);
  }, [loadZones, loadNodes, activeZoneID]);

  const refreshNodes = useCallback(() => {
    loadNodes(activeZoneID);
  }, [loadNodes, activeZoneID]);

  const value = useMemo<IPZoneCtx>(
    () => ({
      zones,
      zonesLoading,
      activeZoneID,
      setActiveZoneID,
      activeNodes,
      nodesLoading,
      refresh,
      refreshNodes,
    }),
    [
      zones,
      zonesLoading,
      activeZoneID,
      setActiveZoneID,
      activeNodes,
      nodesLoading,
      refresh,
      refreshNodes,
    ]
  );

  return (
    <IPZoneContext.Provider value={value}>{children}</IPZoneContext.Provider>
  );
}

export function useIPZone(): IPZoneCtx {
  const ctx = useContext(IPZoneContext);
  if (!ctx) throw new Error("useIPZone must be used inside <IPZoneProvider>");
  return ctx;
}
